import { useState } from "react";
import {
  Table,
  Button,
  Input,
  message,
  Empty,
  Dropdown,
  Tag,
  Card,
  Space,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EllipsisOutlined,
  SettingOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  SearchOutlined,
  UserOutlined,
  AppstoreOutlined,
  BranchesOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTenantsQuery, useToggleTenantStatusMutation } from "../../Services";
import { PageHeader } from "../../components";
import { tablePagination } from "../../components";
import { PAGE_SIZE, ROUTE } from "../../Constant";
import {
  ConfigNameManageDrawer,
  CreateTenantDrawer,
  PackageManageDrawer,
  SystemVersionDrawer,
} from "./components";

export const TenantsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [configNameDrawerOpen, setConfigNameDrawerOpen] = useState(false);
  const [packageDrawerOpen, setPackageDrawerOpen] = useState(false);
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);

  const { data: allTenants, isLoading, refetch } = useTenantsQuery();

  const filteredTenants = (allTenants || []).filter((tenant) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(search) ||
      tenant.tenant_sub_name?.toLowerCase().includes(search)
    );
  });

  const toggleStatusMutation = useToggleTenantStatusMutation();

  const activeTenants = (allTenants || []).filter(
    (t) => t.status === "active",
  ).length;
  const inactiveTenants = (allTenants || []).filter(
    (t) => t.status === "inactive",
  ).length;

  const handleToggleStatus = (record: any) => {
    const newStatus = record.status === "active" ? "inactive" : "active";
    toggleStatusMutation.mutate(
      { id: record.id, status: newStatus },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(res.message || "Status updated.");
          } else {
            message.error(res?.message || "Failed to update status.");
          }
        },
        onError: () => message.error("Failed to update status."),
      },
    );
  };

  const actionsColumn = {
    title: "Actions",
    key: "actions",
    width: 80,
    render: (_: any, record: any) => {
      const isActive = record.status === "active";
      const items = [
        {
          key: "config",
          icon: <SettingOutlined style={{ color: "#1890ff" }} />,
          label: "System Config",
          onClick: () =>
            navigate(
              `/system-admin/${ROUTE.system_admin_tenants}/${record.id}`,
            ),
        },
        { type: "divider" as const },
        {
          key: "toggle",
          icon: isActive ? (
            <StopOutlined style={{ color: "#ff4d4f" }} />
          ) : (
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          ),
          label: isActive ? "Deactivate" : "Activate",
          danger: isActive,
          onClick: () => handleToggleStatus(record),
        },
      ];

      return (
        <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
          <Button
            type="text"
            size="small"
            icon={<EllipsisOutlined />}
            onClick={(e) => e.preventDefault()}
            className="hover:bg-gray-100 hover:rounded-md"
          />
        </Dropdown>
      );
    },
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Sub Name",
      dataIndex: "tenant_sub_name",
      key: "tenant_sub_name",
      sorter: (a, b) =>
        (a.tenant_sub_name || "").localeCompare(b.tenant_sub_name || ""),
      render: (v: string) => v || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (v: string) => (
        <Tag
          color={v === "active" ? "green" : "red"}
          icon={v === "active" ? <CheckCircleOutlined /> : <StopOutlined />}
          className="border-none font-medium"
        >
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : "-"),
    },
    actionsColumn,
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Tenant Management"
        subText="Create and manage system tenants"
        handleButtons={[
          {
            icon: ReloadOutlined,
            title: "Refresh",
            onClick: () => refetch(),
          },
          {
            icon: DatabaseOutlined,
            title: "Config Names",
            onClick: () => setConfigNameDrawerOpen(true),
          },
          {
            icon: AppstoreOutlined,
            title: "Packages",
            onClick: () => setPackageDrawerOpen(true),
          },
          {
            icon: BranchesOutlined,
            title: "System Version",
            onClick: () => setVersionDrawerOpen(true),
          },
          {
            icon: PlusOutlined,
            title: "Create Tenant",
            onClick: () => setDrawerOpen(true),
          },
        ]}
      />

      {(filteredTenants.length > 0 || searchTerm || isLoading) && (
        <div className="mb-4 flex flex-col md:flex-row gap-4 justify-between">
          <Space size="middle">
            <Card className="shadow-sm border-gray-200 min-w-[140px]">
              <div className="flex items-center gap-2">
                <UserOutlined style={{ color: "#1890ff" }} />
                <div>
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {allTenants?.length || 0}
                  </div>
                </div>
              </div>
            </Card>
            <Card className="shadow-sm border-green-200 min-w-[140px]">
              <div className="flex items-center gap-2">
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <div>
                  <div className="text-xs text-gray-500">Active</div>
                  <div className="text-lg font-semibold text-green-700">
                    {activeTenants}
                  </div>
                </div>
              </div>
            </Card>
            <Card className="shadow-sm border-red-200 min-w-[140px]">
              <div className="flex items-center gap-2">
                <StopOutlined style={{ color: "#ff4d4f" }} />
                <div>
                  <div className="text-xs text-gray-500">Inactive</div>
                  <div className="text-lg font-semibold text-red-700">
                    {inactiveTenants}
                  </div>
                </div>
              </div>
            </Card>
          </Space>

          <Space>
            <Input
              placeholder="Search by name or sub name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#999" }} />}
              allowClear
              className="w-full md:w-64 h-8 rounded-lg border-gray-300"
            />
          </Space>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <Table
          dataSource={filteredTenants}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total: filteredTenants.length,
            onChange: setPage,
          })}
          locale={{ emptyText: <Empty description="No tenants found" /> }}
          className="rounded-lg overflow-hidden"
          rowClassName="hover:bg-gray-50 transition-colors cursor-default"
        />
      </div>

      <CreateTenantDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <ConfigNameManageDrawer
        open={configNameDrawerOpen}
        onClose={() => setConfigNameDrawerOpen(false)}
      />

      <PackageManageDrawer
        open={packageDrawerOpen}
        onClose={() => setPackageDrawerOpen(false)}
      />

      <SystemVersionDrawer
        open={versionDrawerOpen}
        onClose={() => setVersionDrawerOpen(false)}
      />
    </div>
  );
};
